import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { InviteSummaryBanner } from './InviteSummaryBanner';

interface InviteSummaryDialogProps {
  onClose: () => void;
}

export function InviteSummaryDialog({ onClose }: InviteSummaryDialogProps) {
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    contentRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  if (typeof document === 'undefined') {
    return null;
  }

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === overlayRef.current) {
      onClose();
    }
  };

  return createPortal(
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 backdrop-blur-md px-4 py-6 sm:px-6 sm:py-10"
      onMouseDown={handleBackdropClick}
    >
      <div
        ref={contentRef}
        className="relative w-full max-w-2xl overflow-hidden rounded-3xl border border-ai-surface-glow/40 bg-ai-surface-elevated/95 shadow-ai-card focus:outline-none focus-visible:ring focus-visible:ring-ai-focus"
        role="dialog"
        aria-modal="true"
        aria-label={t('memberPage.invite.label')}
        tabIndex={-1}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full border border-ai-surface-glow/30 text-sm text-ai-text-secondary transition-colors hover:border-ai-primary/60 hover:text-ai-text-primary focus:outline-none focus-visible:ring focus-visible:ring-ai-focus sm:right-5 sm:top-5 sm:h-10 sm:w-10"
          aria-label={t('app.actions.close')}
          onClick={onClose}
        >
          X
        </button>
        <div className="max-h-[calc(100vh-4rem)] overflow-y-auto p-5 sm:p-8">
          <InviteSummaryBanner />
        </div>
      </div>
    </div>,
    document.body
  );
}
